import { useRef } from 'react';
import type { ISchemaContainer } from '../../../common/validation/schemaContainer';
import { updateValidationErrorsForOne } from '../validation';
import { useStateWithPartialSet } from './useStateWithPartialSet';
import { useValidationErrors } from './useValidationErrors';

export function useFormHandler<T extends object, Schema extends ISchemaContainer<any>>(
    schemaContainer: Schema,
    formItem: T
) {
    const [item, setItem] = useStateWithPartialSet(formItem);
    const [errors, setErrors] = useValidationErrors();
    const itemRef = useRef(item);

    function handleChange<K extends keyof T>(key: K, value: T[K]) {
        // Unfortunately the casting is required due to a bug in typescript, see https://github.com/Microsoft/TypeScript/issues/14473
        setItem({ [key]: value } as unknown as Partial<T>);
        // We ref the new item so we can correctly validate it (instead of the old one)
        itemRef.current = { ...item, [key]: value };
    }

    function validateOne(key: Extract<keyof T, string>) {
        setErrors(updateValidationErrorsForOne(schemaContainer, itemRef.current, errors, key));
    }

    return [item, errors, { handleChange, validateOne, setItem, setErrors }] as const;
}
